import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';
import './ActiveFixturesPage.css';

function ActiveFixturesPage({ pageTitle = 'Summary' }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'with_engines', 'without_engines'
  const [sortBy, setSortBy] = useState('start'); // 'start' | 'handle' | 'engines' | 'slips'
  const [sortOrder, setSortOrder] = useState('asc');

  const loadActiveFixtures = useCallback(async ({ showSpinner = false } = {}) => {
    try {
      if (showSpinner) setLoading(true);
      const { data: payload } = await apiClient.get('/api/fixtures/active/summary');
      setData(payload);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      console.error('Failed to load active fixtures:', err);
      setError('Failed to load active fixtures');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadActiveFixtures({ showSpinner: true });
    const interval = setInterval(() => loadActiveFixtures({ showSpinner: false }), 30000);
    return () => clearInterval(interval);
  }, [loadActiveFixtures]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortBy(column);
    // Handle / engines / slips default to highest-first; start stays soonest-first.
    setSortOrder(column === 'start' ? 'asc' : 'desc');
  };

  const sortIndicator = (column) => {
    if (sortBy !== column) return '';
    return sortOrder === 'desc' ? ' ↓' : ' ↑';
  };

  const filteredFixtures = useMemo(() => {
    if (!data?.fixtures) return [];
    let rows;
    switch (filter) {
      case 'with_engines':
        rows = data.fixtures.filter((f) => f.total_engines > 0);
        break;
      case 'without_engines':
        rows = data.fixtures.filter((f) => f.total_engines === 0);
        break;
      default:
        rows = data.fixtures;
    }

    const dir = sortOrder === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (sortBy === 'handle') {
        return dir * ((Number(a.player_pick_handle) || 0) - (Number(b.player_pick_handle) || 0));
      }
      if (sortBy === 'engines') {
        return dir * ((Number(a.total_engines) || 0) - (Number(b.total_engines) || 0));
      }
      if (sortBy === 'slips') {
        return dir * ((Number(a.player_pick_slips) || 0) - (Number(b.player_pick_slips) || 0));
      }
      // start: missing times always last
      const aT = a.start_time_utc || '';
      const bT = b.start_time_utc || '';
      if (!aT && !bT) return 0;
      if (!aT) return 1;
      if (!bT) return -1;
      const cmp = aT < bT ? -1 : aT > bT ? 1 : 0;
      return cmp * dir;
    });
  }, [data, filter, sortBy, sortOrder]);

  const formatHoursUntil = (hours) => {
    if (hours === null || hours === undefined) return '—';
    if (hours < 0) {
      const absHours = Math.abs(hours);
      if (absHours < 1) return `${Math.round(absHours * 60)}m ago`;
      if (absHours < 24) return `${absHours.toFixed(1)}h ago`;
      return `${Math.round(absHours / 24)}d ago`;
    }
    if (hours < 1) return `in ${Math.round(hours * 60)}m`;
    if (hours < 24) return `in ${hours.toFixed(1)}h`;
    return `in ${Math.round(hours / 24)}d`;
  };

  const getTimeClass = (hours) => {
    if (hours === null || hours === undefined) return '';
    if (hours < 0) return 'time-past';
    if (hours < 2) return 'time-soon';
    if (hours < 24) return 'time-today';
    return 'time-future';
  };

  const formatMoney = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    const abs = Math.abs(n);
    const sign = n < 0 ? '−' : '';
    if (abs >= 1000) return `${sign}$${(abs / 1000).toFixed(1)}k`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  const goToGame = (fixtureId) => {
    if (!fixtureId) return;
    navigate(`/game/${encodeURIComponent(fixtureId)}`);
  };

  const handleRowKeyDown = (event, fixtureId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      goToGame(fixtureId);
    }
  };

  if (loading && !data) {
    return (
      <div className="active-fixtures-page">
        <div className="loading">Loading active fixtures...</div>
      </div>
    );
  }

  const summary = data?.summary || {};

  return (
    <div className="active-fixtures-page">
      <div className="page-header">
        <h1>{pageTitle}</h1>
        {lastUpdate && (
          <div className="last-update">
            Last updated: {lastUpdate.toLocaleTimeString()}
            {loading ? ' · refreshing…' : ''}
          </div>
        )}
        <button className="button" onClick={() => loadActiveFixtures({ showSpinner: false })}>
          Refresh
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{summary.total_fixtures_polled || 0}</div>
          <div className="stat-label">Fixtures Polled</div>
        </div>
        <div className="stat-card clickable" onClick={() => setFilter('with_engines')}>
          <div className="stat-value stat-success">{summary.fixtures_with_engines || 0}</div>
          <div className="stat-label">With Engines</div>
        </div>
        <div className="stat-card clickable" onClick={() => setFilter('without_engines')}>
          <div className="stat-value stat-warning">{summary.fixtures_without_engines || 0}</div>
          <div className="stat-label">No Engines</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary.total_engines || 0}</div>
          <div className="stat-label">Total Engines</div>
        </div>
        <div
          className="stat-card clickable"
          onClick={() => handleSort('handle')}
          title="Sort table by player pick handle"
        >
          <div className="stat-value">{formatMoney(summary.total_player_pick_handle)}</div>
          <div className="stat-label">Player Pick Handle{sortBy === 'handle' ? sortIndicator('handle') : ''}</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{summary.total_player_pick_slips || 0}</div>
          <div className="stat-label">Player Pick Slips</div>
        </div>
      </div>

      <div className="market-types-bar">
        <span className="market-types-label">Market Types:</span>
        {(summary.market_types || []).map(mt => (
          <span key={mt} className="market-type-badge">{mt}</span>
        ))}
        {(summary.market_types || []).length === 0 && (
          <span className="no-markets">No active markets</span>
        )}
      </div>

      <div className="filter-bar">
        <button 
          className={`filter-button ${filter === 'all' ? 'active' : ''}`}
          onClick={() => setFilter('all')}
        >
          All ({data?.fixtures?.length || 0})
        </button>
        <button 
          className={`filter-button ${filter === 'with_engines' ? 'active' : ''}`}
          onClick={() => setFilter('with_engines')}
        >
          With Engines ({summary.fixtures_with_engines || 0})
        </button>
        <button 
          className={`filter-button ${filter === 'without_engines' ? 'active' : ''}`}
          onClick={() => setFilter('without_engines')}
        >
          No Engines ({summary.fixtures_without_engines || 0})
        </button>
      </div>

      <div className="card">
        <h2 className="card-title">
          {filter === 'all' ? 'All Polled Fixtures' : 
           filter === 'with_engines' ? 'Fixtures With Active Engines' : 
           'Fixtures Without Engines'}
        </h2>
        <p className="card-description">
          {filter === 'without_engines' 
            ? 'These fixtures are being polled but have no KF engines (insufficient books or no data yet).'
            : 'Fixtures currently being polled for odds data. Click Start / Handle / Engines / Slips to sort.'}
        </p>
        
        {filteredFixtures.length === 0 ? (
          <p className="no-data">No fixtures match the current filter.</p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th
                  className="sortable"
                  onClick={() => handleSort('start')}
                  title="Sort by kickoff"
                >
                  Start{sortIndicator('start')}
                </th>
                <th>Matchup</th>
                <th>League</th>
                <th
                  className="sortable"
                  onClick={() => handleSort('engines')}
                  title="Sort by engine count"
                >
                  Engines{sortIndicator('engines')}
                </th>
                <th>Players</th>
                <th
                  className="sortable"
                  onClick={() => handleSort('handle')}
                  title="Sort by player pick handle"
                >
                  Handle{sortIndicator('handle')}
                </th>
                <th
                  className="sortable"
                  onClick={() => handleSort('slips')}
                  title="Sort by player pick slips"
                >
                  Slips{sortIndicator('slips')}
                </th>
                <th>Market Types</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredFixtures.map(fixture => (
                <tr
                  key={fixture.fixture_id}
                  className="clickable-row"
                  tabIndex={0}
                  role="link"
                  onClick={() => goToGame(fixture.fixture_id)}
                  onKeyDown={(event) => handleRowKeyDown(event, fixture.fixture_id)}
                  aria-label={`Open game view for ${fixture.home_team || 'home'} vs ${fixture.away_team || 'away'}`}
                >
                  <td className={getTimeClass(fixture.hours_until_start)}>
                    <div className="time-cell">
                      <span className="time-relative">
                        {formatHoursUntil(fixture.hours_until_start)}
                      </span>
                      <span className="time-absolute">
                        {fixture.start_time_utc 
                          ? new Date(fixture.start_time_utc).toLocaleString(undefined, {
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          : '—'}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div className="matchup">
                      <span className="team home">{fixture.home_team || '—'}</span>
                      <span className="vs">vs</span>
                      <span className="team away">{fixture.away_team || '—'}</span>
                    </div>
                  </td>
                  <td>
                    <span className="league-badge">{fixture.league || '—'}</span>
                  </td>
                  <td>
                    <span className={`engine-count ${fixture.total_engines > 0 ? 'has-engines' : 'no-engines'}`}>
                      {fixture.total_engines}
                    </span>
                  </td>
                  <td>{fixture.unique_players}</td>
                  <td>{formatMoney(fixture.player_pick_handle)}</td>
                  <td>{fixture.player_pick_slips || 0}</td>
                  <td>
                    <div className="market-types-cell">
                      {fixture.market_types.map(mt => (
                        <span key={mt} className="market-type-mini">{mt.replace('player_', '')}</span>
                      ))}
                      {fixture.market_types.length === 0 && <span className="no-markets">—</span>}
                    </div>
                  </td>
                  <td>
                    {fixture.hours_until_start !== null && fixture.hours_until_start < 0 ? (
                      <span className="badge badge-warning">Started</span>
                    ) : fixture.total_engines > 0 ? (
                      <span className="badge badge-success">Active</span>
                    ) : (
                      <span className="badge badge-muted">Waiting</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default ActiveFixturesPage;
